import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useRemindClientRequestMutation } from "../../../slices/ClientRequestsSlice";
import { useGetRequestReportDataQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { serverMessage } from "../../../utils/serverMessage";
import { askTable } from "../ask/reportAsk.utils";
import {
  agoText,
  isRequestOverdue,
  remindersText,
  requestStats,
  shortDate,
} from "../moreReports.utils";
import { useReportsAccess } from "../reportsAccess";
import { PeopleClientTypeFilters, useReportFilters } from "../ui/ReportFilters";
import {
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LINK,
  LoadError,
  Loading,
  MiniBar,
  Note,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";

const TABLE = "Open request lists";

/** Client requests (2026-11-05): who still owes documents, and how many reminders went out. */
export const ClientRequestsReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const orgId = getLinkedFirm()?.orgId || "";
  const data = useGetRequestReportDataQuery({ orgId }, { skip: !orgId });
  const [remind] = useRemindClientRequestMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const lists = useMemo(
    () =>
      (data.data?.lists ?? []).filter(
        (l) =>
          (!filters.userIds.length ||
            filters.userIds.includes(l.sentByUserId || "")) &&
          (!filters.clientIds.length ||
            filters.clientIds.includes(l.client.customerId)) &&
          (!filters.workTypeIds.length ||
            filters.workTypeIds.includes(l.workTypeId || "")),
      ),
    [data.data, filters],
  );
  const stats = requestStats(lists);
  const avg = data.data?.averageDaysToComplete;
  const v = (x: React.ReactNode) => (data.isLoading ? "…" : x);
  const shown = useFocusRows(TABLE, lists, (l) => l.id);
  const { pageRows, pager } = usePagedRows(shown);

  const onRemind = async (id: string) => {
    setBusy(id);
    const result = await remind(id);
    setBusy(null);
    if ("error" in result)
      toast.error(
        serverMessage(result.error, "The reminder couldn't be sent."),
      );
    else {
      toast.success("Reminder sent.");
      data.refetch();
    }
  };

  const onExport = () =>
    exportDataToExcel(
      lists.map((l) => ({
        Client: l.client.name,
        "Request list": l.title,
        "Work item": l.workName,
        "Work type": l.workType,
        "Items done": l.itemsDone,
        "Items asked": l.itemsTotal,
        Due: l.dueDate || "",
        Reminders: remindersText(l),
        "Last client activity": l.lastClientActivityAt
          ? shortDate(new Date(l.lastClientActivityAt))
          : "Nothing yet",
        "Sent by": l.sentByName,
      })),
      "Client requests",
    );

  const ask = useMemo(
    () => ({
      title: "Client requests",
      path: "/reports/client-requests",
      description:
        "Request lists sent to clients that aren't finished: progress, due date, reminders and the client's last activity.",
      kpis: [
        { label: "Open lists", value: String(stats.open) },
        { label: "Items still missing", value: String(stats.missing) },
        { label: "Overdue lists", value: String(stats.overdue) },
        {
          label: "Average days to complete",
          value: avg == null ? "—" : `${Math.round(avg)} days`,
        },
      ],
      tables: [
        askTable(
          TABLE,
          lists,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (l) => l.client.name,
            },
            {
              key: "list",
              label: "Request list",
              kind: "text",
              value: (l) => l.title,
            },
            {
              key: "work",
              label: "Work item",
              kind: "text",
              value: (l) => l.workName,
            },
            {
              key: "done",
              label: "Items done",
              kind: "number",
              value: (l) => l.itemsDone,
            },
            {
              key: "total",
              label: "Items asked",
              kind: "number",
              value: (l) => l.itemsTotal,
            },
            { key: "due", label: "Due", kind: "date", value: (l) => l.dueDate },
            {
              key: "reminders",
              label: "Reminders sent",
              kind: "number",
              value: (l) => l.remindersSent,
            },
            {
              key: "activity",
              label: "Last client activity",
              kind: "date",
              value: (l) => l.lastClientActivityAt?.slice(0, 10),
            },
          ],
          (l) => l.id,
          (l) => `/work/${l.workId}`,
        ),
      ],
    }),
    [lists, stats.open, stats.missing, stats.overdue, avg],
  );

  return (
    <ReportPage
      title={"Client requests"}
      description={
        "Which clients still owe you documents or answers, how much is missing and how many reminders have gone out."
      }
      onExport={lists.length ? onExport : undefined}
      ask={data.isLoading ? undefined : ask}
      askSuggestions={[
        "Who should I chase first?",
        "Which lists are overdue?",
        "Which clients never opened their list?",
      ]}
      filters={
        <PeopleClientTypeFilters
          state={state}
          peopleLabel={"Sent by"}
          showPeople={access.firmWide}
        />
      }>
      {data.isError && <LoadError />}
      {data.data && !data.data.available && (
        <Note>
          Client request lists need a database update first. Ask for it to be
          applied, then this report fills in.
        </Note>
      )}
      <KpiGrid>
        <Kpi
          label={"Open lists"}
          value={v(stats.open)}
          note={`for ${stats.clients} client${stats.clients === 1 ? "" : "s"}`}
        />
        <Kpi
          label={"Items still missing"}
          value={v(stats.missing)}
          note={`of ${stats.asked} asked for`}
        />
        <Kpi
          label={"Overdue lists"}
          value={v(stats.overdue)}
          note={"past their due date"}
          tone={stats.overdue ? "red" : "default"}
        />
        <Kpi
          label={"Average time to complete"}
          value={v(avg == null ? "—" : `${Math.round(avg)} days`)}
          note={"lists completed this year"}
        />
      </KpiGrid>

      <Card title={TABLE}>
        {data.isLoading ? (
          <Loading />
        ) : !lists.length ? (
          <Empty
            text={
              "No open request lists. Everything you asked for has come in."
            }
          />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Request list · work item
                </th>
                <th scope={"col"} className={TH}>
                  Progress
                </th>
                <th scope={"col"} className={TH}>
                  Due
                </th>
                <th scope={"col"} className={TH}>
                  Reminders
                </th>
                <th scope={"col"} className={TH}>
                  Last client activity
                </th>
                <th scope={"col"} className={TH}>
                  <span className={"sr-only"}>Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((l) => {
                const late = isRequestOverdue(l);
                const stopped = l.remindersSent >= l.maxReminders;
                return (
                  <tr key={l.id}>
                    <td className={TD}>
                      {l.client.link ? (
                        <Link to={l.client.link} className={LINK}>
                          {l.client.name}
                        </Link>
                      ) : (
                        <span className={"font-bold"}>{l.client.name}</span>
                      )}
                    </td>
                    <td className={`${TD} max-w-[260px] truncate`}>
                      <Link
                        to={`/work/${l.workId}`}
                        className={
                          "text-[#3F4757] no-underline hover:text-[#6B4EFF] hover:underline"
                        }>
                        {l.title} · {l.workType || l.workName}
                      </Link>
                    </td>
                    <td className={TD}>
                      <span className={"inline-flex items-center"}>
                        <MiniBar
                          pct={
                            l.itemsTotal
                              ? (l.itemsDone / l.itemsTotal) * 100
                              : 0
                          }
                          color={"#6B4EFF"}
                          width={80}
                        />
                        <span
                          className={
                            "ml-2 text-[12.5px] font-semibold text-[#3F4757]"
                          }>
                          {l.itemsDone} of {l.itemsTotal}
                        </span>
                      </span>
                    </td>
                    <td
                      className={`${TD} font-semibold ${
                        late ? "text-[#B42318]" : "text-[#3A4256]"
                      }`}>
                      {l.dueDate ? shortDate(l.dueDate) : "—"}
                    </td>
                    <td className={TD}>
                      <span
                        className={`rounded-full px-2.5 py-[3px] text-[12.5px] ${
                          stopped
                            ? "bg-[#FDECEC] font-bold text-[#B42318]"
                            : "bg-[#F4F5F8] font-semibold text-[#3A4256]"
                        }`}>
                        {remindersText(l)}
                      </span>
                    </td>
                    <td className={`${TD} text-[#3F4757]`}>
                      {l.lastClientActivityAt
                        ? agoText(l.lastClientActivityAt)
                        : "Nothing yet"}
                    </td>
                    <td className={TD}>
                      <button
                        type={"button"}
                        disabled={busy === l.id}
                        onClick={() => onRemind(l.id)}
                        className={
                          "h-[34px] rounded-[9px] border border-[#6B4EFF] bg-white px-3 text-[12.5px] font-bold text-[#4B32D6] hover:bg-[#F0EDFF] disabled:opacity-60"
                        }>
                        {busy === l.id ? "Sending…" : "Remind now"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        &quot;6 of 6&quot; reminders = ProCharted has stopped reminding and told
        you. &quot;Remind now&quot; emails the client&apos;s portal people at
        once and doesn&apos;t count as one of the 6.
        {!access.firmWide && " You see lists you sent or on your own work."}
      </Note>
    </ReportPage>
  );
};
